import { useState, useEffect } from 'react';
import { format, addDays, isAfter, isBefore } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar as CalendarIcon, Clock, ArrowLeft, Send, MapPin, Video, User, Mail, Phone, BookOpen, GraduationCap, Check, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useCenterData } from '@/hooks/useCenterData';
import { cn } from '@/lib/utils';
import { validateEmailFormat } from '@/lib/sanitization';
import { naplanTestPackLabels, filterNaplanTestPacksForGrade, clearMismatchedNaplanTestPack } from '@/config/naplanTestPacks';

interface AssessmentBookingData {
  date: Date;
  time: string;
  meetingType: 'in-person' | 'online';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  childName: string;
  courseInterested: string;
  currentGrade: string;
}

interface AssessmentCalendarBookingProps {
  centerSlug: string;
  courseOptions?: string[];
  gradeOptions?: string[];
  hideHeader?: boolean;
  initialFormData?: Partial<AssessmentBookingData>;
  source?: string;
}

const AssessmentCalendarBooking = ({
  centerSlug,
  courseOptions: customCourseOptions,
  gradeOptions: customGradeOptions,
  hideHeader = false,
  initialFormData,
  source = 'direct'
}: AssessmentCalendarBookingProps) => {
  const { data: centerData, isLoading: centerLoading, error: centerError } = useCenterData(centerSlug);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<'date' | 'time' | 'form'>('date');
  const [bookingData, setBookingData] = useState<Partial<AssessmentBookingData>>({
    meetingType: centerSlug === 'the-ponds' ? 'online' : 'in-person',
    ...initialFormData
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [selectedTruganinaLocation, setSelectedTruganinaLocation] = useState<'mainview' | 'koomail'>('koomail');

  // Email validation handler
  const handleEmailChange = (email: string) => {
    setBookingData(prev => ({ ...prev, email }));
    if (email.length > 0) {
      const validation = validateEmailFormat(email);
      setEmailError(validation.isValid ? '' : validation.error || 'Invalid email');
    } else {
      setEmailError('');
    }
  };

  // Assessment time slots: 4pm to 7pm for weekdays, 9am to 5pm for Saturdays
  const weekdayTimeSlots = [
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00'
  ];

  // Truganina weekday slots: 3:30pm to 7:30pm
  const truganinaWeekdayTimeSlots = [
    '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'
  ];

  // Tarneit slots: 4:00pm to 8:00pm (Mon-Wed only)
  const tarneitTimeSlots = [
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'
  ];
  
  const werribeeTimeSlots = ['16:30', '17:00', '17:30', '18:00', '18:30'];

  const saturdayTimeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00'
  ];
  
  const getDefaultTimeSlotsForDate = (date: Date) => {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 6) return saturdayTimeSlots;
    // Truganina has different weekday hours
    if (centerSlug === 'truganina') return truganinaWeekdayTimeSlots;
    // Tarneit: 4pm to 8:30pm (Mon-Wed only)
    if (centerSlug === 'tarneit') return tarneitTimeSlots;
    if (centerSlug === 'werribee') return werribeeTimeSlots;
    return weekdayTimeSlots;
  };

  const defaultCourseOptions = centerData?.state_code === 'VIC' ? [
    'Selective Entry High School Preparation',
    'SEC Exam Preparation',
    'Foundations Course',
    ...naplanTestPackLabels,
    'S.T.E.A.M',
    'General Assessment'
  ] : centerData?.state_code === 'WA' ? [
    'GATE TMRW™ Preparation',
    'Foundations Course',
    ...naplanTestPackLabels,
    'S.T.E.A.M',
    'General Assessment'
  ] : [
    'OC TMRW™ Preparation',
    'Selective TMRW™ Preparation', 
    'Foundations Course',
    ...naplanTestPackLabels,
    'S.T.E.A.M',
    'SCOPE Discussion',
    'General Assessment'
  ];

  const defaultGradeOptions = centerData?.state_code === 'WA' ? [
    'Pre-primary',
    'Year 1',
    'Year 2', 
    'Year 3',
    'Year 4',
    'Year 5',
    'Year 6',
    'Year 7',
    'Year 8',
    'Other'
  ] : [
    'Kindergarten',
    'Year 1',
    'Year 2', 
    'Year 3',
    'Year 4',
    'Year 5',
    'Year 6',
    'Year 7',
    'Year 8',
    'Other'
  ];

  const courseOptions = filterNaplanTestPacksForGrade(
    customCourseOptions || defaultCourseOptions, bookingData.currentGrade,
  );
  const gradeOptions = customGradeOptions || defaultGradeOptions;

  // Re-seed booking data when initialFormData changes (e.g. via diagnostic event)
  useEffect(() => {
    if (!initialFormData) return;
    setBookingData(prev => ({ ...prev, ...initialFormData }));
  }, [initialFormData]);

  // Load available slots when date changes
  useEffect(() => {
    if (selectedDate && centerData) {
      loadAvailableSlots();
    }
  }, [selectedDate, centerData]);

  // Filter out slots that are within 24 hours from current time
  const filterSlotsBy24HourRule = (slots: string[], selectedDate: Date): string[] => {
    const now = new Date();
    const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    
    return slots.filter(slot => {
      // Create datetime for this slot on the selected date
      const [hours, minutes] = slot.split(':').map(Number);
      const slotDateTime = new Date(selectedDate);
      slotDateTime.setHours(hours, minutes, 0, 0);
      
      // Only allow slots that are at least 24 hours from now
      return slotDateTime >= twentyFourHoursFromNow;
    });
  };

  const loadAvailableSlots = async () => {
    if (!selectedDate || !centerData) return;
    
    setIsLoadingSlots(true);
    
    try {
      const calendarId =
        centerSlug === 'caroline-springs'
          ? centerData.email
          : (centerData.franchise_calendar_id || centerData.email);

      const { data, error } = await supabase.functions.invoke('get-available-slots', {
        body: {
          calendarId,
          date: format(selectedDate, 'yyyy-MM-dd'),
          timeZone: centerData.time_zone || 'Australia/Sydney',
          centerSlug: centerSlug
        }
      });

      const defaultSlots = getDefaultTimeSlotsForDate(selectedDate);
      let slotsToUse = defaultSlots;

      if (error) {
        console.error('Error loading available slots:', error);
        toast({
          title: "Calendar Check Unavailable",
          description: "Showing all available time slots. Your booking will be confirmed manually.",
          variant: "default"
        });
      } else {
        if (data.error) {
          console.error('Calendar API error:', data.error);
          toast({
            title: "Calendar Check Unavailable", 
            description: "Showing all available time slots. Your booking will be confirmed manually.",
            variant: "default"
          });
        } else {
          slotsToUse = data.availableSlots || defaultSlots;
        }
      }

      // Apply 24-hour rule to filter out slots within 24 hours
      const filteredSlots = filterSlotsBy24HourRule(slotsToUse, selectedDate);
      setAvailableSlots(filteredSlots);
      
    } catch (error) {
      console.error('Error loading available slots:', error);
      toast({
        title: "Calendar Check Unavailable",
        description: "Showing all available time slots. Your booking will be confirmed manually.", 
        variant: "default"
      });
      // Still apply 24-hour rule even for fallback slots
      const defaultSlots = getDefaultTimeSlotsForDate(selectedDate);
      const filteredSlots = filterSlotsBy24HourRule(defaultSlots, selectedDate);
      setAvailableSlots(filteredSlots);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setBookingData(prev => ({ ...prev, date }));
      setCurrentStep('time');
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setBookingData(prev => ({ ...prev, time }));
    setCurrentStep('form');
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!centerData) {
      toast({
        title: "Error",
        description: "Center information not available. Please try again.",
        variant: "destructive"
      });
      return;
    }

    // Validate all mandatory fields
    const requiredFields: { field: keyof AssessmentBookingData; label: string }[] = [
      { field: 'firstName', label: 'First Name' },
      { field: 'lastName', label: 'Last Name' },
      { field: 'email', label: 'Email' },
      { field: 'phone', label: 'Phone Number' },
      { field: 'childName', label: "Child's Name" },
      { field: 'currentGrade', label: 'Current Grade' },
      { field: 'courseInterested', label: 'Course Interested In' },
    ];

    for (const { field, label } of requiredFields) {
      if (!bookingData[field] || (bookingData[field] as string).trim() === '') {
        toast({
          title: "Required Field Missing",
          description: `Please fill in the "${label}" field before submitting.`,
          variant: "destructive"
        });
        return;
      }
    }

    // Validate email before submission
    const emailValidation = validateEmailFormat(bookingData.email || '');
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.error || 'Please enter a valid email address');
      toast({
        title: "Invalid Email",
        description: emailValidation.error || "Please enter a valid email address",
        variant: "destructive"
      });
      return;
    }

    // Validate Australian phone number client-side
    const phoneCleaned = (bookingData.phone || '').replace(/[^\d+]/g, '');
    const isValidAuPhone =
      (phoneCleaned.startsWith('+61') && phoneCleaned.length === 12 && /^[234578]/.test(phoneCleaned.slice(3))) ||
      (phoneCleaned.startsWith('61') && phoneCleaned.length === 11 && /^[234578]/.test(phoneCleaned.slice(2))) ||
      (phoneCleaned.startsWith('0') && phoneCleaned.length === 10 && /^0[234578]/.test(phoneCleaned));
    if (!isValidAuPhone) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid Australian phone number (e.g., 0412 345 678 or +61 412 345 678).",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const calendarId =
        centerSlug === 'caroline-springs'
          ? centerData.email
          : (centerData.franchise_calendar_id || centerData.email);

      const { data, error } = await supabase.functions.invoke('assessment-booking', {
        body: {
          date: format(selectedDate!, 'yyyy-MM-dd'),
          time: selectedTime,
          meetingType: bookingData.meetingType!,
          firstName: bookingData.firstName!,
          lastName: bookingData.lastName!,
          email: bookingData.email!,
          phone: bookingData.phone!,
          childName: bookingData.childName!,
          courseInterested: bookingData.courseInterested!,
          currentGrade: bookingData.currentGrade!,
          source,
          centerSlug,
          centerConfig: {
            centerName: centerData.name,
            centerAddress: centerSlug === 'truganina' 
              ? 'Koomail Tardy Community Centre, 117 Alcock Rd, Truganina VIC 3029'
              : centerData.address,
            centerPhone: centerData.phone,
            centerEmail: centerData.email,
            calendarId,
            timeZone: centerData.time_zone || 'Australia/Sydney'
          }
        }
      });

      if (error || !data?.success) {
        let serverMessage: string | undefined;
        try {
          const ctx: any = (error as any)?.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            serverMessage = body?.error || body?.message;
          }
        } catch { /* ignore */ }
        serverMessage = serverMessage || (data as any)?.error || (error as any)?.message;
        throw new Error(serverMessage || 'Booking failed');
      }

      const [hours, minutes] = selectedTime.split(':').map(Number);
      const displayTime = `${hours % 12 === 0 ? 12 : hours % 12}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
      
      toast({
        title: "Assessment Booked!",
        description: `Your assessment is scheduled for ${format(selectedDate!, 'EEEE, MMMM do')} at ${displayTime}. You'll receive a confirmation email shortly.`,
      });

      // Reset form
      setSelectedDate(undefined);
      setSelectedTime('');
      setCurrentStep('date');
      setBookingData({ meetingType: 'in-person' });
      setAvailableSlots([]);
      
    } catch (error: any) {
      console.error('Booking error:', error);
      toast({
        title: "Booking Failed",
        description: error?.message || "There was an error scheduling your assessment. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 'form') {
      setCurrentStep('time');
    } else if (currentStep === 'time') {
      setCurrentStep('date');
      setSelectedDate(undefined);
    }
  };

  // Holiday closure dates for specific centers
  // The Ponds: closed until 11th Jan 2026 (bookings from 12th Jan)
  // Colebee, Riverstone: closed until 3rd Jan 2026
  const getHolidayClosureEnd = (slug: string) => {
    if (slug === 'the-ponds') {
      return new Date(2026, 0, 12); // Jan 12th, 2026 (first available after 11th Jan closure)
    }
    if (['colebee', 'riverstone'].includes(slug)) {
      return new Date(2026, 0, 3); // Jan 3rd, 2026
    }
    return null;
  };
  
  const holidayClosureEnd = getHolidayClosureEnd(centerSlug);
  const isHolidayClosure = holidayClosureEnd && new Date() < holidayClosureEnd;
  
  // Get holiday closure message based on center
  const getHolidayClosureMessage = (slug: string) => {
    if (slug === 'the-ponds') {
      return {
        main: "🎄 Our centre is closed for the holidays until 11th January.",
        sub: "Bookings are available from Sunday, 12th January 2026."
      };
    }
    return {
      main: "🎄 Our centre is closed for Christmas & New Year's holidays.",
      sub: "Bookings are available from Friday, 3rd January 2026."
    };
  };
  
  const holidayMessage = getHolidayClosureMessage(centerSlug);
  
  // The Ponds specific: disable Fridays (no slots) and Sundays for other centers
  const isThePondsCenter = centerSlug === 'the-ponds';

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    const maxDate = addDays(today, 60);
    const dayOfWeek = date.getDay();
    
    // Block dates before holiday closure end
    if (holidayClosureEnd && isBefore(date, holidayClosureEnd)) {
      return true;
    }
    
    // The Ponds: block Fridays (no slots available)
    if (isThePondsCenter && dayOfWeek === 5) {
      return true;
    }
    
    // Tarneit: Mon (1) to Fri (5)
    if (centerSlug === 'tarneit' && ![1, 2, 3, 4, 5].includes(dayOfWeek)) {
      return true;
    }
    
    // Point Cook, Truganina & Werribee: block weekends (Sat + Sun)
    if ((centerSlug === 'point-cook' || centerSlug === 'truganina' || centerSlug === 'werribee') && (dayOfWeek === 0 || dayOfWeek === 6)) {
      return true;
    }

    // For The Ponds: allow all days except Friday
    // For other centers: block Sundays
    const blockSunday = !isThePondsCenter && centerSlug !== 'tarneit' && dayOfWeek === 0;
    
    return isBefore(date, today) || 
           isAfter(date, maxDate) || 
           blockSunday;
  };

  // Show loading state while center data loads
  if (centerLoading) {
    return (
      <div className="py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading center information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if center data fails to load
  if (centerError || !centerData) {
    return (
      <div className="py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center">
            <p className="text-destructive">Unable to load center information. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <section className={cn("bg-gradient-to-br from-primary-50 to-white", hideHeader ? "py-8" : "py-20")}>
      <div className="container mx-auto px-4 max-w-4xl">
        {!hideHeader && (
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900 mb-4">
              Book Your Free Assessment at {centerData.name}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {centerSlug === 'the-ponds' 
                ? "Schedule a comprehensive 45-minute assessment to understand your child's learning needs and find the perfect program."
                : "Schedule a comprehensive assessment to understand your child's learning needs and find the perfect program."}
            </p>
          </div>
        )}

        <Card className="bg-white/80 backdrop-blur-sm border border-gray-200/50 shadow-xl rounded-3xl">
          <CardContent className="p-8">
            {/* Step indicator */}
            <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300",
                currentStep === 'date' ? "bg-primary text-primary-foreground" : 
                (currentStep === 'time' || currentStep === 'form') ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
              )}>
                {(currentStep === 'time' || currentStep === 'form') ? <Check className="h-4 w-4" /> : '1'}
              </div>
              <div className="w-12 h-0.5 bg-border"></div>
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300",
                currentStep === 'time' ? "bg-primary text-primary-foreground" : 
                currentStep === 'form' ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
              )}>
                {currentStep === 'form' ? <Check className="h-4 w-4" /> : '2'}
              </div>
              <div className="w-12 h-0.5 bg-border"></div>
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300",
                currentStep === 'form' ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}>
                3
              </div>
            </div>
            </div>

            {/* Date Selection */}
            {currentStep === 'date' && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-2 flex items-center justify-center gap-2">
                    <CalendarIcon className="h-5 w-5" />
                    Select a Date
                  </h3>
                  <p className="text-muted-foreground">Choose a date for your assessment (Monday-Saturday)</p>
                </div>
                
                {/* Holiday Closure Notice */}
                {isHolidayClosure && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-amber-800 text-center font-medium">
                      {holidayMessage.main}
                    </p>
                    <p className="text-amber-700 text-center text-sm mt-1">
                      {holidayMessage.sub}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={isDateDisabled}
                    className="rounded-md border"
                  />
                </div>
              </div>
            )}

            {/* Time Selection */}
            {currentStep === 'time' && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-2 flex items-center justify-center gap-2">
                    <Clock className="h-5 w-5" />
                    Select a Time
                  </h3>
                  <p className="text-muted-foreground">
                    Available slots for {selectedDate && format(selectedDate, 'EEEE, MMMM do')}
                  </p>
                </div>

                {isLoadingSlots ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-muted-foreground">Loading available times...</p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-muted-foreground">
                      {selectedDate && selectedDate.getDay() === 0 ? (
                        <p className="text-lg">Centre is closed on Sundays</p>
                      ) : selectedDate && selectedDate.getDay() === 6 ? (
                        <p className="text-lg">All slots are booked for Saturday</p>
                      ) : (
                        <p className="text-lg">No available slots for this day</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
                    {availableSlots.map((slot) => {
                      const [hours, minutes] = slot.split(':').map(Number);
                      const displayTime = `${hours % 12 === 0 ? 12 : hours % 12}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
                      
                      return (
                        <Button
                          key={slot}
                          variant={selectedTime === slot ? "default" : "outline"}
                          onClick={() => handleTimeSelect(slot)}
                          className="h-12"
                        >
                          {displayTime}
                        </Button>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-center">
                  <Button variant="ghost" onClick={handleBack}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Date
                  </Button>
                </div>
              </div>
            )}

            {/* Form */}
            {currentStep === 'form' && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-2 flex items-center justify-center gap-2">
                    <User className="h-5 w-5" />
                    Assessment Details
                  </h3>
                  <p className="text-muted-foreground">
                    {selectedDate && format(selectedDate, 'EEEE, MMMM do')} at {selectedTime && (() => {
                      const [hours, minutes] = selectedTime.split(':').map(Number);
                      return `${hours % 12 === 0 ? 12 : hours % 12}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
                    })()}
                  </p>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-6 max-w-4xl mx-auto">
                  {/* Meeting Type - Full Width */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Meeting Type</Label>
                    {centerSlug === 'the-ponds' ? (
                      // The Ponds - Online only
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div className="h-4 w-4 rounded-full border-2 border-primary bg-primary flex items-center justify-center">
                            <div className="h-2 w-2 rounded-full bg-white" />
                          </div>
                          <Label className="flex items-center gap-2">
                            <Video className="h-4 w-4" />
                            Online
                          </Label>
                        </div>
                        <div className="ml-6 p-3 bg-muted/50 rounded-lg border">
                          <p className="text-sm text-muted-foreground">
                            💻 <strong>Meeting via:</strong> Google Meet (link will be provided)
                          </p>
                        </div>
                      </div>
                    ) : centerSlug === 'tarneit' ? (
                      // Tarneit - In-person only
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input type="radio" checked readOnly className="accent-primary" />
                          <Label className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            In-Person
                          </Label>
                        </div>
                        <div className="ml-6 p-3 bg-muted/50 rounded-lg border">
                          <p className="text-sm text-muted-foreground">
                            📍 <strong>Location:</strong> {centerData.address}
                          </p>
                        </div>
                      </div>
                    ) : (
                      // All other centers - Both options
                      <RadioGroup
                        value={bookingData.meetingType}
                        onValueChange={(value) => setBookingData(prev => ({ ...prev, meetingType: value as 'in-person' | 'online' }))}
                        className="space-y-4"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="in-person" id="in-person" />
                            <Label htmlFor="in-person" className="flex items-center gap-2 cursor-pointer">
                              <MapPin className="h-4 w-4" />
                              In-Person
                            </Label>
                          </div>
                          {bookingData.meetingType === 'in-person' && (
                            <div className="ml-6 p-3 bg-muted/50 rounded-lg border">
                              {centerSlug === 'truganina' ? (
                                <p className="text-sm text-muted-foreground">
                                  📍 <strong>Location:</strong> Koomail Tardy Community Centre, 117 Alcock Rd | Truganina VIC 3029
                                </p>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  📍 <strong>Location:</strong> {centerData.address}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="online" id="online" />
                            <Label htmlFor="online" className="flex items-center gap-2 cursor-pointer">
                              <Video className="h-4 w-4" />
                              Online
                            </Label>
                          </div>
                          {bookingData.meetingType === 'online' && (
                            <div className="ml-6 p-3 bg-muted/50 rounded-lg border">
                              <p className="text-sm text-muted-foreground">
                                💻 <strong>Meeting via:</strong> Google Meet (link will be provided)
                              </p>
                            </div>
                          )}
                        </div>
                      </RadioGroup>
                    )}
                  </div>

                  {/* Two Column Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column - Parent Details */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground border-b pb-2">Parent/Guardian Information</h4>
                      
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          placeholder="Your first name"
                          value={bookingData.firstName || ''}
                          onChange={(e) => setBookingData(prev => ({ ...prev, firstName: e.target.value }))}
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          placeholder="Your last name"
                          value={bookingData.lastName || ''}
                          onChange={(e) => setBookingData(prev => ({ ...prev, lastName: e.target.value }))}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <div className="relative">
                          <Input
                            id="email"
                            type="email"
                            placeholder="your.email@example.com"
                            value={bookingData.email || ''}
                            onChange={(e) => handleEmailChange(e.target.value)}
                            className={cn(emailError && 'border-red-500 focus-visible:ring-red-500')}
                            required
                          />
                          {emailError && (
                            <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-sm">
                              <AlertCircle className="h-3.5 w-3.5" />
                              <span>{emailError}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="0400 000 000"
                          value={bookingData.phone || ''}
                          onChange={(e) => setBookingData(prev => ({ ...prev, phone: e.target.value }))}
                          required
                        />
                      </div>
                    </div>

                    {/* Right Column - Child Details */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground border-b pb-2">Student Information</h4>
                      
                      <div className="space-y-2">
                        <Label htmlFor="childName">Child's Name *</Label>
                        <Input
                          id="childName"
                          placeholder="Your child's name"
                          value={bookingData.childName || ''}
                          onChange={(e) => setBookingData(prev => ({ ...prev, childName: e.target.value }))}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="currentGrade">Current Grade *</Label>
                        <Select
                          value={bookingData.currentGrade || ''}
                          onValueChange={(value) => setBookingData(prev => ({
                            ...prev,
                            currentGrade: value,
                            courseInterested: clearMismatchedNaplanTestPack(prev.courseInterested, value),
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select current grade" />
                          </SelectTrigger>
                          <SelectContent>
                            {gradeOptions.map((grade) => (
                              <SelectItem key={grade} value={grade}>
                                {grade}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="courseInterested">Course Interested In *</Label>
                        <Select
                          value={bookingData.courseInterested || ''}
                          onValueChange={(value) => setBookingData(prev => ({ ...prev, courseInterested: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select course of interest" />
                          </SelectTrigger>
                          <SelectContent>
                            {courseOptions.map((course) => (
                              <SelectItem key={course} value={course}>
                                {course}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons - Full Width */}
                  <div className="flex gap-4 pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleBack}
                      className="flex-1"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                          Booking...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Book Assessment
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default AssessmentCalendarBooking;